import { CheckCircle, Mail } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ConsultationForm from "@/components/ConsultationForm";
import SEO from "@/components/SEO";
import { useLanguage } from "@/hooks/useLanguage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

const Consultation = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const paymentStatus = searchParams.get('payment');
  const sessionId = searchParams.get('session_id');
  const [processing, setProcessing] = useState(false);
  
  const benefits = [
    t('consult.benefit1'),
    t('consult.benefit2'),
    t('consult.benefit3'),
    t('consult.benefit4'),
    t('consult.benefit5'),
    t('consult.benefit6'),
  ];

  useEffect(() => {
    if (paymentStatus === 'success' && sessionId && !processing) {
      setProcessing(true);
      
      // Process the payment and send notifications
      supabase.functions
        .invoke('process-consultation-payment', {
          body: { sessionId },
        })
        .then(({ error }) => {
          if (error) {
            console.error('Error processing consultation payment:', error);
          } else {
            console.log('Consultation payment processed successfully');
          }
        })
        .finally(() => {
          setProcessing(false);
        });
    }
  }, [paymentStatus, sessionId, processing]);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Schedule Legal Consultation - Daniel Albert Law Firm"
        description="Schedule your legal consultation with Daniel Albert Law Firm. Qualified legal advice in immigration, family law, criminal defense, and more. Call (832) 930-3059."
        useMapping={true}
      />
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('consult.title')}</h1>
            <p className="text-xl opacity-95">
              {t('consult.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          {paymentStatus === 'success' ? (
            /* Success Message */
            <div className="max-w-2xl mx-auto">
              {processing && (
                <div className="mb-6 flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>
                    {language === 'es'
                      ? 'Procesando y enviando confirmaciones...'
                      : language === 'vi'
                      ? 'Đang xử lý và gửi xác nhận...'
                      : 'Processing and sending confirmations...'}
                  </span>
                </div>
              )}
              <Card className="border-2 border-accent">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center">
                    <CheckCircle className="h-10 w-10 text-accent" />
                  </div>
                  <CardTitle className="text-3xl font-bold">
                    {language === 'es' 
                      ? '¡Gracias por su solicitud!'
                      : language === 'vi'
                      ? 'Cảm ơn bạn đã gửi yêu cầu!'
                      : 'Thank you for your submission!'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 text-muted-foreground mb-4">
                      <Mail className="h-5 w-5" />
                      <p>
                        {language === 'es'
                          ? 'La confirmación será enviada por correo electrónico'
                          : language === 'vi'
                          ? 'Xác nhận sẽ được gửi qua email'
                          : 'Confirmation will be sent via email'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-muted/50 p-6 rounded-lg">
                    <h3 className="font-semibold mb-3">
                      {language === 'es'
                        ? '¿Qué sigue?'
                        : language === 'vi'
                        ? 'Bước tiếp theo?'
                        : "What's Next?"}
                    </h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <span>
                          {language === 'es'
                            ? 'Recibirá un correo de confirmación con los detalles de su consulta'
                            : language === 'vi'
                            ? 'Bạn sẽ nhận được email xác nhận với chi tiết tư vấn'
                            : 'You will receive a confirmation email with your consultation details'}
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <span>
                          {language === 'es'
                            ? 'Nuestro equipo se pondrá en contacto con usted para confirmar su cita'
                            : language === 'vi'
                            ? 'Nhóm của chúng tôi sẽ liên hệ để xác nhận cuộc hẹn'
                            : 'Our team will contact you to confirm your appointment'}
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <span>
                          {language === 'es'
                            ? 'Prepare cualquier documento relevante para su consulta'
                            : language === 'vi'
                            ? 'Chuẩn bị các tài liệu liên quan cho cuộc tư vấn'
                            : 'Prepare any relevant documents for your consultation'}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="text-center pt-4 space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {language === 'es'
                        ? '¿Preguntas? Contáctenos al'
                        : language === 'vi'
                        ? 'Có câu hỏi? Liên hệ'
                        : 'Questions? Contact us at'}{' '}
                      <a href="tel:8329303059" className="font-semibold text-primary hover:underline">
                        (832) 930-3059
                      </a>
                    </p>
                    <Button 
                      onClick={() => window.location.href = '/'}
                      variant="outline"
                    >
                      {language === 'es'
                        ? 'Volver al inicio'
                        : language === 'vi'
                        ? 'Quay về trang chủ'
                        : 'Return to Home'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Benefits */}
              <div>
                <h2 className="text-3xl font-bold mb-6">{t('consult.expect')}</h2>
                <p className="text-lg text-muted-foreground mb-8">
                  {t('consult.expect.desc')}
                </p>

                <h3 className="text-xl font-bold mb-4">{t('consult.during')}</h3>
                <ul className="space-y-4 mb-8">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                  <h3 className="text-lg font-bold mb-2">{t('consult.note')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('consult.note.desc')}
                  </p>
                </div>
              </div>

              {/* Consultation Form */}
              <div>
                <ConsultationForm />
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Consultation;
